/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { setNameTextStyle } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'rankingHorizontalBar',
  name_en: 'Ranking Horizontal Bar',
  // name_zh: '排名条形图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 3,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
  };
}

/** 暂不支持legend点击功能 */
export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const presetOption = hook.buildStandardChartPresetOption(api);
    const chartViewRect = $dom.getBoundingClientRect();
    const {
      dataIndex = 0,
      encoding: { x, y }
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    let mappedNameList = new Set();
    const dataset = data.values.map(item => {
      mappedNameList.add(`${item[y]}`);
      let value = +item[x];
      if (Number.isNaN(value)) {
        value = 0;
      }
      return {
        __origin: item,
        name: `${item[y]}`,
        value
      };
    });

    mappedNameList = Array.from(mappedNameList);

    // * ------------------------- chart
    const updateFrequency = 1200;
    const colorList = presetOption?.color || [
      '#FF9500',
      '#14CCBD',
      '#1A9FFF',
      '#3366FF',
      '#4433FF',
      '#FF33AA',
      '#CC41D9',
      '#FFAD29',
      '#38D9C6',
      '#42B7FF',
      '#5C8AFF',
      '#6F5CFF',
      '#FF5CB6',
      '#DF6AE6',
      '#FFC252',
      '#60E6D2',
      '#6BCBFF',
      '#85ABFF',
      '#9785FF',
      '#FF85C4',
      '#F196F2',
      '#FFD57A',
      '#8DF2E0',
      '#94DDFF',
      '#ADCAFF',
      '#BCADFF',
      '#FFADD5',
      '#FFC7FE'
    ];

    // 配置项
    const legend = {
      data: mappedNameList.map((item, index) => ({
        name: item,
        color: colorList[index % colorList.length] || 'blue',
        type: util.const.SERIES_2_LEGEND_TYPE_MAP.$$common,
        selected: true,
        disableSelect: true
      })),
      show: false,
      selected: mappedNameList.reduce((pre, cur) => {
        pre[cur] = true;
        return pre;
      }, {})
    };
    const baseGridOption = Array.isArray(presetOption.grid)
      ? presetOption.grid[0]
      : presetOption.grid!;
    const grid = {
      ...baseGridOption,
      top: 14
    };
    const yMaxNum = Math.floor((chartViewRect.height - grid.top - grid.bottom) / 15);

    const option = {
      legend,
      animationDuration: updateFrequency,
      animationDurationUpdate: updateFrequency,
      grid: [
        {
          ...grid
        }
      ],
      tooltip: {
        formatter: item => {
          const value = hook.formatDataNumberValue(item.value, {
            type: 'table',
            unit: dataMetaInfo[item.seriesName]?.unit
          });
          // prettier-ignore
          const itemObj = {
            type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
            color: item.color,
            name: item.name,
            value: value.value + value.unit
          }
          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: item?.axisValueLabel,
            items: [itemObj]
          });
        }
      },
      xAxis: [
        {
          type: 'value',
          show: true,
          axisLine: {
            show: true
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          position: 'top',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          dvPadding: [75, 0],
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      yAxis: [
        {
          show: true,
          data: mappedNameList,
          type: 'category',
          name: y,
          inverse: true,
          nameLocation: 'start',
          nameTextStyle: {
            verticalAlign: 'top',
            align: 'left',
            padding: [-20, 0, 0, 10]
          },
          max: Math.min(mappedNameList.length - 1, yMaxNum - 1),
          axisLine: {
            onZero: false,
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false
          },
          axisLabel: {
            show: true,
            inside: true,
            margin: 0,
            formatter(value, index) {
              return `{rank${index > 2 ? '' : index + 1}|${index + 1}}  {name|${`${value}  `}}`;
            },
            rich: {
              name: {
                color: presetOption?.yAxis?.axisLabel?.color
              },
              rank: {
                color: presetOption?.yAxis?.axisLabel?.color,
                fontSize: 12,
                fontWeight: 500
              },
              rank1: {
                color: 'rgb(250, 59, 50)',
                fontSize: 12,
                fontWeight: 500
              },
              rank2: {
                color: 'rgb(255, 167, 66)',
                fontSize: 12,
                fontWeight: 500
              },
              rank3: {
                color: 'rgb(255, 214, 66)',
                fontSize: 12,
                fontWeight: 500
              }
            }
          },
          animationDuration: updateFrequency,
          animationDurationUpdate: updateFrequency
        }
      ],
      series: [
        {
          realtimeSort: true,
          type: 'bar',
          itemStyle: {
            color(param) {
              return colorList[mappedNameList.findIndex(n => param.name === n) % colorList.length];
            }
          },
          data: dataset,
          label: {
            fontWeight: 400,
            formatter(params) {
              const result = hook.formatDataNumberValue(params.value, {
                type: 'table',
                unit: dataMetaInfo[params.seriesName]?.unit
              });
              return `{value|${result.value + result.unit}}`;
            },
            show: true,
            valueAnimation: true,
            position: 'right',
            rich: {
              value: {
                color: presetOption?.yAxis?.axisLabel?.color
              },
              n: {
                fontSize: 13
              },
              v: {
                fontSize: 12
              }
            }
          }
        }
      ],
      graphic: {
        elements: [
          {
            type: 'text',
            right: 'center',
            bottom: 'center',
            style: {
              font: `bold ${
                Math.min(chartViewRect.width, chartViewRect.height) / 8
              }px THSJinRongTi-Medium`,
              // fill: 'rgba(0, 0, 0, 0.08)'
              fill: 'rgba(0, 0, 0, 0)'
            }
          }
        ]
      }
    };
    setNameTextStyle($dom, option);
    return option;
  }
});
